import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CalendarClock, RefreshCw, Loader2, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, ArrowRight, AlertTriangle, Building2, Sparkles,
  Landmark, ShieldAlert,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine,
} from "recharts";
import TrendChart from "@/components/FinanceTrendChart";

const CASHFLOW_TREND_SERIES = [
  { key: "opening_balance", label: "Opening balance", color: "#0284c7", width: 3 },
  { key: "min_running_balance", label: "12-mo trough", color: "#dc2626" },
  { key: "final_balance", label: "Horizon balance", color: "#059669" },
];

const fmtMoney = (v) => new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", maximumFractionDigits: 0,
}).format(v || 0);
const fmtMoney2 = (v) => new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", maximumFractionDigits: 2,
}).format(v || 0);
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-AU")} ${d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}`;
};
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Build a Monday-based 6-week calendar grid for a given year/month.
function buildCalendar(year, month) {
  const first = new Date(year, month - 1, 1);
  const startOffset = (first.getDay() + 6) % 7; // 0=Mon
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const dayNum = i - startOffset + 1;
    if (dayNum < 1 || dayNum > daysInMonth) {
      cells.push(null);
    } else {
      const iso = `${year}-${String(month).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
      cells.push({ day: dayNum, iso });
    }
  }
  // trim trailing empty rows
  while (cells.length > 35 && cells.slice(-7).every((c) => c === null)) {
    cells.splice(-7, 7);
  }
  return cells;
}

// Group calendar cells into weeks of 7 for the sidebar totals.
function groupCellsIntoWeeks(cells) {
  const rows = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }
  return rows;
}

export default function CashflowForecastPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [monthIdx, setMonthIdx] = useState(0); // 0 = current month
  const [selectedTenant, setSelectedTenant] = useState("_master");

  const load = useCallback(async (tenantId = selectedTenant) => {
    setLoading(true);
    try {
      const params = tenantId && tenantId !== "_master"
        ? { params: { tenant_id: tenantId } }
        : {};
      const { data } = await api.get("/finance-holdings/cashflow-forecast", params);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load cashflow forecast");
    }
    setLoading(false);
  }, [selectedTenant]);
  useEffect(() => {
    setMonthIdx(0);
    load(selectedTenant);
  // Intentionally omit `load` from deps — it is stable per tenant.
  }, [selectedTenant, load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const params = selectedTenant && selectedTenant !== "_master"
        ? { params: { tenant_id: selectedTenant } }
        : {};
      const { data } = await api.post("/finance-holdings/cashflow-forecast/refresh", null, params);
      setData(data);
      toast.success("Cashflow forecast refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    }
    setRefreshing(false);
  };

  const months = data?.months || [];
  const days = data?.days || {};
  const activeMonth = months[monthIdx];

  const calendarCells = useMemo(() => {
    if (!activeMonth) return [];
    return buildCalendar(activeMonth.year, activeMonth.month_num);
  }, [activeMonth]);

  const weekRows = useMemo(() => groupCellsIntoWeeks(calendarCells), [calendarCells]);

  // For a given week row, sum income + expenses of the cells that
  // fall inside the active month (we ignore leading/trailing nulls).
  const weekTotals = weekRows.map((row) => {
    let inc = 0; let exp = 0;
    for (const cell of row) {
      if (!cell) continue;
      const d = days[cell.iso];
      if (d) { inc += d.income; exp += d.expenses; }
    }
    return { income: inc, expenses: exp, net: inc - exp };
  });

  return (
    <div className="p-6 max-w-[1400px] space-y-5" data-testid="cashflow-forecast-page">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-sky-100 text-sky-700">
          <CalendarClock className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Finance &amp; Payroll</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Cashflow Forecast</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
            12-month rolling forecast built from open Xero invoices (ACCREC → income) and
            open bills (ACCPAY → expenses), plus repeating-invoice templates and projected
            BAS / PAYG / Super lodgements + payroll cadence. Overdue items are surfaced
            on today so nothing slips through.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(data?.available_tenants?.length || 0) > 0 && (
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger className="h-9 w-64 text-xs"
                data-testid="cashflow-tenant-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_master">
                  <span className="flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5" />
                    <span className="font-semibold">Master · all entities combined</span>
                  </span>
                </SelectItem>
                {(data.available_tenants || []).map((t) => (
                  <SelectItem key={t.tenant_id} value={t.tenant_id}>
                    {t.tenant_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {data && (
            <span className="text-[11px] text-zinc-500">
              {data.from_cache ? "Cached" : "Fresh"} · {fmtDateTime(data.created_at)}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}
            data-testid="cashflow-refresh-btn">
            {refreshing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh from Xero
          </Button>
        </div>
      </div>

      {/* Cash health summary */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-sky-200 bg-sky-50/30"
            data-testid="cashflow-opening-card">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-1.5">
                <Landmark className="h-3 w-3" />Opening cash balance
              </div>
              <div className={`text-2xl font-bold mt-1 ${(data.opening_balance || 0) < 0 ? "text-red-700" : "text-sky-800"}`}>
                {fmtMoney(data.opening_balance || 0)}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Sum of Bank-type account balances across every included Xero tenant, as of today.
              </div>
            </CardContent>
          </Card>
          <Card className={`${(data.min_running_balance || 0) < 0 ? "border-red-300 bg-red-50/40" : "border-emerald-200 bg-emerald-50/30"}`}
            data-testid="cashflow-min-balance-card">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-1.5">
                {(data.min_running_balance || 0) < 0
                  ? <ShieldAlert className="h-3 w-3 text-red-500" />
                  : <TrendingUp className="h-3 w-3 text-emerald-600" />}
                Lowest projected balance (next 12 mo)
              </div>
              <div className={`text-2xl font-bold mt-1 ${(data.min_running_balance || 0) < 0 ? "text-red-700" : "text-emerald-800"}`}>
                {fmtMoney(data.min_running_balance || 0)}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                {data.first_negative_day
                  ? <>First goes negative on <b>{new Date(data.first_negative_day).toLocaleDateString("en-AU")}</b> — plan a top-up before then.</>
                  : "Balance stays positive across the whole horizon."}
              </div>
            </CardContent>
          </Card>
          <Card className="border-zinc-200 bg-zinc-50/40"
            data-testid="cashflow-final-balance-card">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex items-center gap-1.5">
                <ArrowRight className="h-3 w-3" />Projected balance at horizon
              </div>
              <div className={`text-2xl font-bold mt-1 ${(data.final_balance || 0) < 0 ? "text-red-700" : "text-zinc-900"}`}>
                {fmtMoney(data.final_balance || 0)}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">
                Opening + net income − net expenses over the next 12 months.
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Running-balance chart */}
      {data && Object.keys(data.days || {}).length > 0 && (
        <Card className="border-zinc-200" data-testid="cashflow-running-chart-card">
          <CardContent className="p-4">
            <div className="text-xs font-semibold mb-2 flex items-center gap-1.5">
              <TrendingDown className="h-3.5 w-3.5 text-zinc-500" />
              Projected running balance — 12 months
            </div>
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <LineChart
                  data={Object.entries(data.days || {})
                    .sort((a, b) => a[0].localeCompare(b[0]))
                    .map(([d, v]) => ({ date: d, balance: v.running_balance || 0 }))}
                  margin={{ top: 8, right: 16, bottom: 4, left: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }}
                    tickFormatter={(iso) => {
                      const d = new Date(iso);
                      return d.toLocaleDateString("en-AU", { month: "short", day: "numeric" });
                    }} minTickGap={40} />
                  <YAxis tick={{ fontSize: 10 }}
                    tickFormatter={(v) => fmtMoney(v)} width={90} />
                  <Tooltip
                    formatter={(v) => fmtMoney2(v)}
                    labelFormatter={(iso) => new Date(iso).toLocaleDateString("en-AU", {
                      weekday: "short", day: "numeric", month: "long", year: "numeric",
                    })} />
                  <ReferenceLine y={0} stroke="#dc2626" strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="balance" stroke="#0284c7"
                    strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[10px] text-zinc-400 mt-1 italic">
              The red dashed line marks $0. Cells where the projected running balance drops
              below that line will run into overdraft.
            </div>
          </CardContent>
        </Card>
      )}

      {/* 12-month strip */}
      <Card className="border-zinc-200">
        <CardContent className="p-3 overflow-x-auto">
          <div className="flex gap-1.5 min-w-max">
            {months.map((m, i) => {
              const active = i === monthIdx;
              return (
                <button
                  type="button"
                  key={m.month}
                  onClick={() => setMonthIdx(i)}
                  data-testid={`cashflow-month-strip-${m.month}`}
                  className={`shrink-0 w-32 text-left px-3 py-2 rounded-sm border transition-colors ${
                    active
                      ? "border-sky-500 bg-sky-50"
                      : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                    {MONTH_NAMES[m.month_num - 1].slice(0, 3)} {m.year}
                  </div>
                  <div className={`text-sm font-bold mt-0.5 ${m.net >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {fmtMoney(m.net)}
                  </div>
                  <div className="flex items-center gap-1 text-[10px] mt-0.5">
                    <TrendingUp className="h-2.5 w-2.5 text-emerald-600" />
                    <span className="text-emerald-700">{fmtMoney(m.income)}</span>
                  </div>
                  <div className="flex items-center gap-1 text-[10px]">
                    <TrendingDown className="h-2.5 w-2.5 text-red-500" />
                    <span className="text-red-600">{fmtMoney(m.expenses)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Calendar + sidebar */}
      {activeMonth && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          <Card className="border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <Button variant="ghost" size="sm"
                  onClick={() => setMonthIdx(Math.max(0, monthIdx - 1))}
                  disabled={monthIdx === 0}
                  data-testid="cashflow-prev-month">
                  <ChevronLeft className="h-4 w-4 mr-1" />Prev
                </Button>
                <div className="text-xl font-bold" data-testid="cashflow-active-month-heading">
                  {MONTH_NAMES[activeMonth.month_num - 1]} {activeMonth.year}
                </div>
                <Button variant="ghost" size="sm"
                  onClick={() => setMonthIdx(Math.min(months.length - 1, monthIdx + 1))}
                  disabled={monthIdx === months.length - 1}
                  data-testid="cashflow-next-month">
                  Next<ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
              {/* weekday header */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {WEEKDAY_HEADERS.map((wd) => (
                  <div key={wd} className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-semibold text-center py-1">
                    {wd}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarCells.map((cell, idx) => {
                  if (!cell) {
                    return <div key={`e-${idx}`} className="h-24 rounded-sm bg-zinc-50/60" />;
                  }
                  const d = days[cell.iso];
                  const isToday = cell.iso === new Date().toISOString().slice(0, 10);
                  const hasOverdue = d?.items?.some((it) => it.is_overdue);
                  return (
                    <Popover key={cell.iso}>
                      <PopoverTrigger asChild>
                        <button type="button"
                          data-testid={`cashflow-day-${cell.iso}`}
                          className={`h-24 rounded-sm border text-left px-2 py-1.5 transition-colors ${
                            isToday
                              ? "border-sky-500 bg-sky-50/40"
                              : "border-zinc-200 hover:border-zinc-300 bg-white hover:bg-zinc-50"
                          }`}>
                          <div className="flex items-center justify-between">
                            <div className={`text-xs font-semibold ${isToday ? "text-sky-700" : "text-zinc-500"}`}>
                              {cell.day}
                            </div>
                            {hasOverdue && (
                              <AlertTriangle className="h-3 w-3 text-amber-500" />
                            )}
                          </div>
                          {d ? (
                            <div className="space-y-0.5 mt-1">
                              {d.income > 0 && (
                                <div className="text-[10.5px] text-emerald-700 font-semibold truncate">
                                  +{fmtMoney(d.income)}
                                </div>
                              )}
                              {d.expenses > 0 && (
                                <div className="text-[10.5px] text-red-600 font-semibold truncate">
                                  −{fmtMoney(d.expenses)}
                                </div>
                              )}
                              {(d.income > 0 || d.expenses > 0) && (
                                <div className={`text-[10px] ${d.net >= 0 ? "text-emerald-700" : "text-red-600"} pt-0.5 border-t border-zinc-100`}>
                                  net {fmtMoney(d.net)}
                                </div>
                              )}
                              {d.running_balance != null && (
                                <div className={`text-[10px] font-semibold ${d.running_balance < 0 ? "text-red-700" : "text-sky-700"}`}>
                                  bal {fmtMoney(d.running_balance)}
                                </div>
                              )}
                            </div>
                          ) : null}
                        </button>
                      </PopoverTrigger>
                      {d && (d.items?.length || 0) > 0 && (
                        <PopoverContent align="start" className="w-80 max-h-96 overflow-y-auto p-3"
                          data-testid={`cashflow-day-popover-${cell.iso}`}>
                          <div className="text-xs font-semibold mb-2">
                            {new Date(cell.iso).toLocaleDateString("en-AU", {
                              weekday: "long", day: "numeric", month: "long", year: "numeric",
                            })}
                          </div>
                          <div className="text-[11px] text-zinc-500 mb-2">
                            +{fmtMoney2(d.income)} · −{fmtMoney2(d.expenses)} · net {fmtMoney2(d.net)}
                          </div>
                          <div className="space-y-1.5">
                            {d.items.map((it, i) => (
                              <div key={`${it.invoice_number}-${i}`}
                                className={`text-[11px] p-2 rounded-sm border ${
                                  it.type === "ACCREC"
                                    ? "border-emerald-200 bg-emerald-50/60"
                                    : "border-red-200 bg-red-50/60"
                                }`}>
                                <div className="flex items-center justify-between">
                                  <div className="font-semibold truncate">
                                    {it.type === "ACCREC" ? "+" : "−"}{fmtMoney2(it.amount)}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {it.is_repeating && (
                                      <Badge variant="outline"
                                        className="text-[8.5px] px-1 py-0 h-4 bg-sky-50 text-sky-700 border-sky-200">
                                        repeating
                                      </Badge>
                                    )}
                                    {it.is_projected && (
                                      <Badge variant="outline"
                                        className="text-[8.5px] px-1 py-0 h-4 bg-violet-50 text-violet-700 border-violet-200"
                                        title={`Projected · ${it.source || "unknown"}`}>
                                        <Sparkles className="h-2.5 w-2.5 mr-0.5" />projected
                                      </Badge>
                                    )}
                                    {it.is_overdue && (
                                      <Badge variant="outline"
                                        className="text-[8.5px] px-1 py-0 h-4 bg-amber-50 text-amber-700 border-amber-200">
                                        overdue
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="text-zinc-600 truncate">{it.contact}</div>
                                <div className="text-zinc-400 text-[10px]">
                                  {it.invoice_number} · {it.tenant_name}
                                </div>
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      )}
                    </Popover>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Sidebar — weekly totals */}
          <div className="space-y-3">
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {MONTH_NAMES[activeMonth.month_num - 1]} total
                </div>
                <div className={`text-2xl font-extrabold mt-1 ${activeMonth.net >= 0 ? "text-emerald-800" : "text-red-700"}`}
                  data-testid="cashflow-month-total-net">
                  {fmtMoney(activeMonth.net)}
                </div>
                <div className="flex items-center gap-3 text-xs text-zinc-600 mt-1.5">
                  <span className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-emerald-600" />
                    {fmtMoney(activeMonth.income)}
                  </span>
                  <ArrowRight className="h-3 w-3 text-zinc-400" />
                  <span className="flex items-center gap-1">
                    <TrendingDown className="h-3 w-3 text-red-500" />
                    {fmtMoney(activeMonth.expenses)}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-zinc-200">
              <CardContent className="p-4">
                <div className="text-xs font-semibold mb-2">Weekly totals</div>
                <div className="space-y-2">
                  {weekTotals.map((w, i) => {
                    const row = weekRows[i];
                    const firstReal = row.find((c) => c);
                    const lastReal = [...row].reverse().find((c) => c);
                    const label = firstReal && lastReal
                      ? `${firstReal.day}–${lastReal.day}`
                      : `Week ${i + 1}`;
                    if (w.income === 0 && w.expenses === 0) {
                      return (
                        <div key={`w-${i}`} className="text-[11px] text-zinc-400 flex items-center justify-between"
                          data-testid={`cashflow-week-row-${i}`}>
                          <span>Week {label}</span>
                          <span className="italic">no activity</span>
                        </div>
                      );
                    }
                    return (
                      <div key={`w-${i}`} className="border-b border-zinc-100 pb-1.5 last:border-b-0"
                        data-testid={`cashflow-week-row-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-zinc-500">Week {label}</span>
                          <span className={`text-sm font-bold ${w.net >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                            {fmtMoney(w.net)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] mt-0.5">
                          <span className="text-emerald-700">+{fmtMoney(w.income)}</span>
                          <span className="text-red-600">−{fmtMoney(w.expenses)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {data?.totals && (
              <Card className="border-zinc-200 bg-zinc-50/40">
                <CardContent className="p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    12-month rolling total
                  </div>
                  <div className={`text-xl font-extrabold mt-1 ${data.totals.net >= 0 ? "text-emerald-800" : "text-red-700"}`}
                    data-testid="cashflow-rolling-total-net">
                    {fmtMoney(data.totals.net)}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-zinc-600 mt-1.5">
                    <span className="text-emerald-700">+{fmtMoney(data.totals.income)}</span>
                    <span className="text-red-600">−{fmtMoney(data.totals.expenses)}</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {loading && !data && (
        <Card className="border-dashed border-zinc-300">
          <CardContent className="p-10 text-center text-zinc-500 text-sm">
            <Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin" />
            Building 12-month forecast from Xero…
          </CardContent>
        </Card>
      )}

      {/* Historical trend — only meaningful on the master view */}
      {selectedTenant === "_master" && (
        <TrendChart kind="cashflow" days={90} series={CASHFLOW_TREND_SERIES} />
      )}
    </div>
  );
}
